import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { AuthentipetLogo } from '../../../components/AuthentipetLogo';
import { AuthLayout } from './AuthLayout';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { authService } from '../../../services/authService';
import { APP_CONSTANTS } from '../../../constants';
import { logger } from '../../../lib/logger';

export const ForgotPassword: React.FC = () => {
    const { theme } = useTheme();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            await authService.resetPasswordForEmail(email);
            setSuccessMessage('Password reset instructions have been sent to your email.');
        } catch (error: unknown) {
            logger.error('Forgot password error', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout>
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                <div className="flex justify-center">
                    <div className="bg-white/10 p-2 rounded-full">
                        <AuthentipetLogo size={80} />
                    </div>
                </div>
                <h2 className="mt-6 text-center text-3xl font-bold font-league-spartan uppercase tracking-[-2px] text-storgia-teal">
                    Reset Password
                </h2>
                <p className={`mt-2 text-center text-sm ${theme === 'dark' ? 'text-storgia-light-blurple/80' : 'text-gray-600'}`}>
                    Enter your email to receive reset instructions
                </p>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                <form className="space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="p-3 rounded bg-red-500/10 border border-red-500/20 text-red-500 text-sm text-center animate-pulse">
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="p-3 rounded bg-green-500/10 border border-green-500/20 text-green-500 text-sm text-center">
                            {successMessage}
                        </div>
                    )}

                    <Input
                        label="Email address"
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="owner@management.com"
                        leftIcon={<Mail className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
                        disabled={isLoading || !!successMessage}
                    />

                    <div>
                        <Button
                            type="submit"
                            disabled={isLoading || !!successMessage}
                            className="w-full bg-storgia-cta-pink hover:bg-storgia-cta-pink/90 text-white shadow-[0_0_20px_rgba(192,0,84,0.3)]"
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                    </div>
                </form>

                <div className="mt-6 text-center">
                    <Link to={APP_CONSTANTS.ROUTES.LOGIN} className={`flex items-center justify-center gap-2 text-sm font-medium transition-all ${theme === 'dark' ? 'text-storgia-light-blurple hover:text-white' : 'text-storgia-blurple hover:text-storgia-dark-blurple'
                        }`}>
                        <ArrowLeft className="h-4 w-4" />
                        Back to Login
                    </Link>
                </div>
            </div>
        </AuthLayout>
    );
};
